import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import { Copy, Plus, X } from "lucide-react";
import { useTranslationApp } from "../../../lib/i18next";
import {
  TEMPLATES_ADD_SLIDE,
  TEMPLATES_DELETE_SLIDE,
  TEMPLATES_DUPLICATE_SLIDE,
  TEMPLATES_ANALYZING_LOCK,
  TEMPLATES_SLIDE_LABEL,
  TEMPLATES_UPLOAD_REFERENCE,
} from "../../../i18n/keysTranslations";
import { artboardChipSx } from "./ArtboardSwitcher";
import { canAddSlide, canRemoveSlide } from "../utils/slides";

const SortableSlideChip = ({
  id,
  index,
  active,
  disabled,
  analyzing,
  uploadingGuide,
  canDelete,
  onSelect,
  onDuplicate,
  onDelete,
}) => {
  const { t } = useTranslationApp();
  const theme = useTheme();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: disabled || analyzing });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 2 : 1,
        opacity: isDragging ? 0.85 : 1,
      }}
    >
      <Box
        component="button"
        type="button"
        disabled={disabled}
        onClick={() => onSelect(id)}
        title={
          analyzing
            ? t(TEMPLATES_ANALYZING_LOCK)
            : uploadingGuide
              ? t(TEMPLATES_UPLOAD_REFERENCE)
              : undefined
        }
        sx={{
          ...artboardChipSx({ active, disabled, theme }),
          cursor: disabled ? "not-allowed" : isDragging ? "grabbing" : "grab",
          opacity: analyzing || uploadingGuide ? 0.72 : undefined,
        }}
        {...attributes}
        {...listeners}
      >
        {t(TEMPLATES_SLIDE_LABEL, { index: index + 1 })}
        {active ? (
          <>
            <Box
              component="span"
              onPointerDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                if (disabled || analyzing) return;
                onDuplicate(id);
              }}
              sx={{
                display: "inline-flex",
                ml: 0.25,
                opacity: 0.8,
                "&:hover": { opacity: 1 },
              }}
              title={t(TEMPLATES_DUPLICATE_SLIDE)}
            >
              <Copy size={12} />
            </Box>
            {canDelete ? (
              <Box
                component="span"
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.stopPropagation();
                  if (disabled || analyzing) return;
                  onDelete(id);
                }}
                sx={{
                  display: "inline-flex",
                  ml: 0.25,
                  opacity: 0.8,
                  "&:hover": { opacity: 1 },
                }}
                title={t(TEMPLATES_DELETE_SLIDE)}
              >
                <X size={12} />
              </Box>
            ) : null}
          </>
        ) : null}
      </Box>
    </Box>
  );
};

const SlideSwitcher = ({
  slides = [],
  activeSlideID,
  disabled,
  analyzingSlideIDs = [],
  uploadingGuideSlideIDs = [],
  onSelect,
  onReorder,
  onDuplicate,
  onDelete,
  onAdd,
}) => {
  const { t } = useTranslationApp();
  const theme = useTheme();
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    })
  );
  const slideIDs = slides.map((slide) => slide.id).filter(Boolean);
  const canDelete = canRemoveSlide({ slides });
  const canAdd = canAddSlide({ slides });

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        flexWrap: "wrap",
      }}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={({ active, over }) => {
          if (!over || active.id === over.id) return;
          onReorder?.({ activeID: active.id, overID: over.id });
        }}
      >
        <SortableContext
          items={slideIDs}
          strategy={horizontalListSortingStrategy}
        >
          {slideIDs.map((id, index) => (
            <SortableSlideChip
              key={id}
              id={id}
              index={index}
              active={id === activeSlideID}
              disabled={disabled}
              analyzing={analyzingSlideIDs.includes(id)}
              uploadingGuide={uploadingGuideSlideIDs.includes(id)}
              canDelete={canDelete}
              onSelect={onSelect}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
            />
          ))}
        </SortableContext>
      </DndContext>
      <Box
        component="button"
        type="button"
        disabled={disabled || !canAdd}
        onClick={() => {
          if (disabled || !canAdd) return;
          onAdd?.();
        }}
        sx={artboardChipSx({
          active: false,
          disabled: disabled || !canAdd,
          theme,
        })}
        title={t(TEMPLATES_ADD_SLIDE)}
      >
        <Plus size={14} />
        {t(TEMPLATES_ADD_SLIDE)}
      </Box>
    </Box>
  );
};

export default SlideSwitcher;
